/**
 * Tipos alinhados ao proxy GettelApi + contrato Zenvia v2 (templates WhatsApp) e campanhas internas.
 */

export type ZenviaTemplateCategory = 'AUTHENTICATION' | 'MARKETING' | 'UTILITY';

/** Componentes WhatsApp no formato objeto (OpenAPI Zenvia templates.components.whatsapp). */
export interface ZenviaWhatsappTemplateComponents {
  header?: ZenviaTemplateHeaderComponent;
  body?: ZenviaTemplateBodyComponent;
  footer?: ZenviaTemplateFooterComponent;
  buttons?: ZenviaTemplateButtonsBlock;
}

export interface ZenviaTemplateBodyComponent {
  type: 'TEXT_FIXED' | 'TEXT_TEMPLATE';
  text: string;
}

export interface ZenviaTemplateHeaderComponent {
  type: 'MEDIA_DOCUMENT' | 'MEDIA_IMAGE' | 'MEDIA_VIDEO' | 'TEXT_FIXED' | 'TEXT_TEMPLATE';
  text?: string;
}

export interface ZenviaTemplateFooterComponent {
  type: 'TEXT_FIXED';
  text: string;
}

export type ZenviaButtonItem =
  | { type: 'URL'; text: string; url: string }
  | { type: 'PHONE_NUMBER'; text: string; phoneNumber: string }
  | { type: 'QUICK_REPLY'; text: string; payload: string };

export interface ZenviaTemplateButtonsMixed {
  type: 'MIXED';
  items: ZenviaButtonItem[];
}

export type ZenviaTemplateButtonsBlock = ZenviaTemplateButtonsMixed;

/** POST /api/v1/zenvia/templates — espelha ZenviaTemplateCreateRequest (OpenAPI templates.create.base + channel). */
export interface ZenviaTemplateCreatePayload {
  name: string;
  locale: string;
  channel: string;
  category: ZenviaTemplateCategory | string;
  senderId?: string;
  components: ZenviaWhatsappTemplateComponents;
  examples?: Record<string, string>;
  notificationEmail?: string;
  /** Metadados opcionais (nao viram variaveis do template na Zenvia). */
  metadata?: Record<string, unknown>;
}

/** PATCH /api/v1/zenvia/templates/{id} — espelha ZenviaTemplatePatchRequest (templates.partial-template). */
export interface ZenviaTemplatePatchPayload {
  locale: string;
  category: ZenviaTemplateCategory | string;
  components: ZenviaWhatsappTemplateComponents;
  examples?: Record<string, string>;
  /** Incluído pelo wizard em todo PATCH (pode ser string vazia). */
  notificationEmail: string;
}

/** Item normalizado na listagem (vários formatos crus da Zenvia são achatados aqui). */
export interface ZenviaTemplateListItem {
  id: string;
  name: string;
  category: string;
  status: string;
  locale: string;
  channel: string;
  senderId?: string;
  /** Nomes das variáveis que o template espera (read-only da Zenvia). */
  fields?: string[];
  /** Texto agregado de todos os componentes (read-only da Zenvia). */
  text?: string;
  /** Resumo dos componentes: header/body/footer/buttons presentes. */
  componentSummary?: string;
  /** Tipo do header: TEXT_FIXED, TEXT_TEMPLATE, MEDIA_IMAGE, etc. */
  headerType?: string;
  /** Texto do header (se houver). */
  headerText?: string;
  /** Texto do body. */
  bodyText?: string;
  /** Texto do footer (se houver). */
  footerText?: string;
  /** Quantidade de botões. */
  buttonCount?: number;
  /** Labels dos botões (ex: ["Acessar site", "Ligar"]). */
  buttonLabels?: string[];
  /** Objeto original da API (debug / detalhe rápido). */
  raw?: unknown;
}

export interface ZenviaSendTemplateTestRequest {
  to: string;
  templateId: string;
  fields?: Record<string, string>;
}

export interface ZenviaSendTemplateTestResponse {
  success: boolean;
  messageId?: string;
  error?: string;
}

export interface ZenviaCampaignRecipientInput {
  phone: string;
  fields?: Record<string, string>;
}

export interface CreateZenviaCampaignRequest {
  name: string;
  description?: string;
  templateId: string;
  defaultFields?: Record<string, string>;
  recipients: ZenviaCampaignRecipientInput[];
}

export interface ZenviaCampaignListItem {
  id: string;
  name: string;
  templateId: string;
  status: string;
  totalRecipients: number;
  pendingCount: number;
  sentCount: number;
  failedCount: number;
  createdAt: string;
}

export interface ZenviaCampaignDetail extends ZenviaCampaignListItem {
  description?: string;
  defaultFields?: Record<string, string>;
}

/** Linha de destinatário (status após processamento pelo job). */
export interface ZenviaCampaignRecipientRow {
  id: string;
  phone: string;
  status: string;
  externalMessageId?: string | null;
  errorMessage?: string | null;
  /** Codigo curto do erro (REJECTED, NOT_DELIVERED, etc.). Vem do MESSAGE_STATUS Zenvia. */
  errorCode?: string | null;
  /** Payload bruto do messageStatus para debug detalhado. */
  errorRawJson?: string | null;
  processedAt?: string | null;
  fields?: Record<string, string> | null;
}

export interface ZenviaCampaignRecipientsPage {
  items: ZenviaCampaignRecipientRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ZenviaCampaignListResponse {
  items: ZenviaCampaignListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CsvPreviewWarning {
  /** field_too_long | mojibake | empty_phone */
  code: string;
  column?: string;
  affectedRows: number;
  limit?: number;
  maxLength?: number;
  message: string;
}

export interface CsvPreviewResponse {
  columns: string[];
  phoneColumnDetected: string;
  totalRows: number;
  sampleRows: Record<string, string>[];
  warnings?: CsvPreviewWarning[];
}

export interface ZenviaCampaignListFilter {
  status?: string;
  search?: string;
  createdFrom?: string;
  createdTo?: string;
}

export interface ZenviaCampaignRecipientsFilter {
  status?: string;
  search?: string;
  errorContains?: string;
  hasError?: boolean;
}

export interface ZenviaCampaignErrorBreakdownItem {
  errorPattern: string;
  count: number;
}

export interface ZenviaCampaignMetrics {
  campaignId: string;
  totalRecipients: number;
  pendingCount: number;
  sentCount: number;
  failedCount: number;
  failureRatePercent: number;
  startedAt?: string | null;
  lastProcessedAt?: string | null;
  errorBreakdown: ZenviaCampaignErrorBreakdownItem[];
}

export type ZenviaRecreateSource = 'FailedOnly' | 'SelectedIds' | 'SelectedPhones' | 'AllNonSent';

export interface ZenviaRecreateCampaignRequest {
  name?: string;
  templateId?: string;
  source: ZenviaRecreateSource;
  recipientIds?: string[];
  phones?: string[];
  /** Padroes de erro a IGNORAR (case-insensitive contains). */
  skipErrorPatterns?: string[];
  autoStart?: boolean;
  defaultFields?: Record<string, string>;
}

export interface CsvCampaignMetadata {
  name: string;
  description?: string;
  templateId: string;
  defaultFields?: Record<string, string>;
  phoneColumn?: string;
  /** Mapeamento variável_do_template → coluna_do_csv. */
  columnMapper?: Record<string, string>;
}

export interface UploadCsvCampaignResponse {
  id: string;
  recipientCount: number;
  columnsDetected: string[];
}

function extractTemplateArray(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    const keys = ['items', 'data', 'content', 'templates', 'results', 'elements'];
    for (const k of keys) {
      const v = o[k];
      if (Array.isArray(v)) return v;
    }
    const page = o.page;
    if (page && typeof page === 'object') {
      const c = (page as Record<string, unknown>).content;
      if (Array.isArray(c)) return c;
    }
  }
  return [];
}

function extractComponentDetails(r: Record<string, unknown>) {
  const comps = r.components as Record<string, unknown> | undefined;
  if (!comps || typeof comps !== 'object') return {};

  const parts: string[] = [];
  let headerType: string | undefined;
  let headerText: string | undefined;
  let bodyText: string | undefined;
  let footerText: string | undefined;
  let buttonCount = 0;
  const buttonLabels: string[] = [];

  const header = comps.header as Record<string, unknown> | undefined;
  if (header && typeof header === 'object') {
    headerType = String(header.type ?? '');
    headerText = header.text != null ? String(header.text) : undefined;
    const hLabel = headerType?.startsWith('MEDIA_') ? headerType.replace('MEDIA_', '').toLowerCase() : 'texto';
    parts.push(`Header (${hLabel})`);
  }

  const body = comps.body as Record<string, unknown> | undefined;
  if (body && typeof body === 'object') {
    bodyText = body.text != null ? String(body.text) : undefined;
    const hasVars = String(body.type ?? '') === 'TEXT_TEMPLATE';
    parts.push(hasVars ? 'Body (com variáveis)' : 'Body (fixo)');
  }

  const footer = comps.footer as Record<string, unknown> | undefined;
  if (footer && typeof footer === 'object') {
    footerText = footer.text != null ? String(footer.text) : undefined;
    parts.push('Footer');
  }

  const buttons = comps.buttons as Record<string, unknown> | undefined;
  if (buttons && typeof buttons === 'object') {
    const items = buttons.items as Array<Record<string, unknown>> | undefined;
    if (Array.isArray(items)) {
      buttonCount = items.length;
      for (const btn of items) {
        if (btn.text) buttonLabels.push(String(btn.text));
      }
      parts.push(`${buttonCount} botão(ões)`);
    }
  }

  return {
    componentSummary: parts.length > 0 ? parts.join(' · ') : undefined,
    headerType,
    headerText,
    bodyText,
    footerText,
    buttonCount: buttonCount > 0 ? buttonCount : undefined,
    buttonLabels: buttonLabels.length > 0 ? buttonLabels : undefined,
  };
}

/** Lê `senderId` do JSON de um template (lista ou GET /templates/{id}). */
export function extractZenviaTemplateSenderId(raw: unknown): string | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const sid = r.senderId;
  if (sid == null) return undefined;
  const s = String(sid).trim();
  return s.length > 0 ? s : undefined;
}

/** Normaliza resposta da listagem de templates (array ou envelope). */
export function normalizeTemplateListResponse(raw: unknown): ZenviaTemplateListItem[] {
  return extractTemplateArray(raw).map((x) => {
    const r = x as Record<string, unknown>;
    const fieldsRaw = r.fields;
    const fields = Array.isArray(fieldsRaw) ? fieldsRaw.map(String) : undefined;
    const text = r.text != null ? String(r.text) : undefined;
    const details = extractComponentDetails(r);
    const senderFromItem = extractZenviaTemplateSenderId(x);

    return {
      id: String(r.id ?? r.templateId ?? ''),
      name: String(r.name ?? r.nome ?? ''),
      category: String(r.category ?? r.categoria ?? ''),
      status: String(r.status ?? ''),
      locale: String(r.locale ?? r.linguagem ?? 'pt_BR'),
      channel: String(r.channel ?? 'WHATSAPP'),
      senderId: senderFromItem,
      fields: fields && fields.length > 0 ? fields : undefined,
      text,
      ...details,
      raw: x,
    };
  });
}
